/* @flow */
import React from 'react';
import type { Map } from 'immutable';
import { connect } from 'react-redux';
import classnames from 'classnames/bind';
import { IO } from 'trustar/fp';
import createDebugger from 'debug';
import bowser from 'bowser';
import { toLower } from 'ramda';
import {
  Spinner,
  ConfirmationModal,
  SessionExpiryWarning
} from 'trustar/components';
import { Link } from 'react-router';

const debug = createDebugger('app:components:App'); // eslint-disable-line no-unused-vars

import s from './App.styl';
const cx = classnames.bind(s);
import { actions, getters } from 'modules/app';
import * as Profile from 'modules/profile';
import * as Enclave from 'modules/enclave';
import * as Constellation from 'modules/constellation';
import * as Sectors from 'modules/sectors';
import * as Subscriptions from 'modules/subscriptions';
import * as SubscriptionManagement from 'modules/subscription-management';
import * as Errors from 'modules/errors';
import * as DataFeed from 'modules/data-feeds';
import { LIGHT_THEME, DARK_THEME } from 'trustar-2.0/themes/common/constants';

import FloatingAlertList from './alerts/FloatingAlertList.js';
import Header from './AppHeader.js';
import { resetFavicon } from 'modules/app/utils';
import Browse from './browse/Browse';
import ConnectedDesignSystemProvider from '../components/ConnectedDesignProvider';

const isDev = process.env.NODE_ENV === 'development';
const browser = IO(() => bowser.name).map(toLower);

const SuperSpinner = (
  <div className={cx('SuperSpinner')}>
    <Spinner />
  </div>
);

const UpsellModal = connect(null, {
  hide: Profile.actions.hideUpsellModal
})(({ hide }) => {
  return (
    <div className={cx('UpsellModal')}>
      <div className={cx('backdrop')} onClick={hide} />
      <div className={cx('inner')}>
        <p>
          You have exceeded your daily API quota. Consider upgrading to increase
          your quota.
          <a href='https://support.trustar.co/article/m5kl5anpiz-api-rate-limit-quota'>
            Why am I seeing this?
          </a>
        </p>
        <div className={cx('buttons')}>
          <a href='https://www.splunk.com/en_us/investor-relations/acquisitions/trustar.html' className={cx('learnMore')} onClick={hide}>
            Upgrade
          </a>
          <a href='#' className={cx('notNow')} onClick={hide}>
            Not Now
          </a>
        </div>
      </div>
    </div>
  );
});

/**
 * The App. App gets passed to ReactDOM.render and is the base of the
 * entire app.
 */
type AppProps = {
  children: any,
  isAdmin: boolean,
  appLoading: boolean,
  options: Map<any, any>,
  timeoutWarning: boolean,
  confirmation: Map<any, any>,
  confirm: Function,
  initialize: Function,
  fetchSectors: Function,
  fetchEnclaves: Function,
  fetchEnclaveTags: Function,
  initializeProfile: Function,
  fetchTagAttributes: Function,
  closeTimeoutWarning: Function,
  fetchSubscriptions: Function,
  fetchManagement: Function,
  fetchFeeds: Function,
  setConstellationOptions: Function,
  showUpsellModal: boolean,
  toggleTheme: Function,
  throwGlobalError: Function,
  updatePreferences: Function,
};
class App extends React.Component {
  props: AppProps;

  componentDidMount() {
    // Reset the favicon: is this still necessary?
    resetFavicon();
    this.props.initialize();
    this.props.fetchEnclaves();
    this.props.fetchSectors();
    this.props.initializeProfile();
    this.props.fetchSubscriptions();
    this.props.fetchFeeds();
    if (this.props.isAdmin) {
      this.props.fetchManagement();
    }
  }

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
  }

  removeTimeoutWarning = () => {
    this.props.closeTimeoutWarning();
  };

  componentDidCatch = (error, info) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(error, info);
    } else {
      this.props.throwGlobalError({ error, info });
    }
  };

  handleShortcuts = (e: Object) => {
    if (!isDev) return;
    if (e.shiftKey && e.key === 'D') {
      this.props.toggleTheme();
    }
  };

  render() {
    const {
      options,
      isAdmin,
      appLoading,
      confirmation,
      timeoutWarning,
      showUpsellModal
    } = this.props;
    const theme = options.get('theme');
    const adminClass = isAdmin ? 'isAdmin' : '';
    const browserName = browser.foldOr(() => 'browser-unknown');

    /* NOTE: We add the _global_ theme class so that we can utilize it from
     * other styl files, not just App.styl
     *
     * NOTE: The browserName is mainly for selective styling in IE. The IE
     * classname is 'msie'. There is also `msedge`. See:
     * https://github.com/ded/bowser#browser-flags
     */

    if (appLoading) return SuperSpinner;

    return (
      <div
        id='App'
        className={cx('App', theme, adminClass) + ` ${theme} ${browserName}`}
        onKeyPress={this.handleShortcuts}>
        <Header />
        <div className={cx('contentArea')}>
          <ConnectedDesignSystemProvider>
            <Browse />
          </ConnectedDesignSystemProvider>
          <div className={cx('routeHandler')}>{this.props.children}</div>
        </div>

        <FloatingAlertList />
        {timeoutWarning && (
          <SessionExpiryWarning
            removeTimeoutWarning={this.removeTimeoutWarning}
          />
        )}
        <ConfirmationModal data={confirmation} confirm={this.props.confirm} />
        {showUpsellModal && <UpsellModal loading={appLoading} />}
        <div id='portalTarget'>
          {/*
            Portals can be rendered here and still be within the main app div,
            which means they can use the :global(.dark) selector to help with
            styling for the dark theme
          */}
        </div>
      </div>
    );
  }
}

const mapState = state => ({
  options: getters.options(state),
  appLoading: getters.loading(state),
  appTheme: getters.theme(state),
  isAdmin: Profile.getters.adminRole(state),
  timeoutWarning: getters.timeoutWarning(state),
  showUpsellModal: Profile.getters.showUpsellModal(state)
});

const dispatchProps = {
  initialize: actions.initialize,
  fetchSectors: Sectors.actions.fetch,
  fetchEnclaves: Enclave.actions.fetch,
  initializeProfile: Profile.actions.fetch,
  closeTimeoutWarning: actions.closeTimeoutWarning,
  setConstellationOptions: Constellation.actions.setOptions,
  fetchSubscriptions: Subscriptions.actions.fetchSubscriptions,
  fetchManagement: SubscriptionManagement.actions.fetchSubscriptions,
  fetchFeeds: DataFeed.actions.fetchFeeds,
  setTheme: actions.setOptions,
  throwGlobalError: Errors.actions.throwGlobalError,
  updatePreferences: Profile.actions.updatePreferences,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...dispatchProps,
  ...stateProps,
  toggleTheme: () => {
    const updatedTheme = stateProps.theme === LIGHT_THEME ? DARK_THEME : LIGHT_THEME;

    dispatchProps.updatePreferences({
      themeMode: updatedTheme.toUpperCase(),
    });
  }
});

export default connect(mapState, dispatchProps, mergeProps)(App);



// WEBPACK FOOTER //
// ./src/app/components/App.js